import { DataTableProps, TableColumn } from "@/components-v2/DataTable";
import { isCellReadonly, sortTableData } from "@/components-v2/dataTableHelpers";
import {
  DCEvent,
  DownloadFileEvent,
  HttpEvent,
  httpPost,
  showError,
  showGeneralError,
  thunk,
} from "@/events/eventsDomain";
import { FileUploadConfig } from "@/fileUpload/fileUploadDomain";
import { I18nState, prepI18nState } from "@/i18n/i18nDomain";
import { Language } from "@/i18n/languages";
import { logError } from "@/utils/log";
import { get } from "lodash";
import { action } from "mobx";

export type ExcelNumericFormat = "PERCENT";

export type ExcelCellData = {
  readonly?: boolean;
  colSpan?: number;
} & (ExcelStringCellData | ExcelNumericCellData | ExcelFormulaCellData | ExcelDateCellData);

export type ExcelStringCellData = {
  type: "string";
  value?: string;
};

export type ExcelNumericCellData = {
  type: "numeric";
  value?: number;
  format?: ExcelNumericFormat;
};

export type ExcelFormulaCellData = {
  type: "formula";
  value?: string;
};

export type ExcelDateCellData = {
  type: "date";
  value?: string;
};

export type ExcelRowData = { cells: ExcelCellData[] };

export type ExcelConstraint = ExcelListConstraint;

export type ExcelListConstraint = { type: "list-constraint"; values: string[] };

export type ExcelColumn = {
  name: string;
  label: string;
  constraints?: ExcelConstraint[];
};

export type ExcelData = {
  columns: ExcelColumn[];
  rows: ExcelRowData[];
};

export type CreateExcelWorkbookRequest = { filename: string; data: ExcelData };

export type ExtractExcelDataResponse = { data: ExcelData };

export type ExcelApi = {
  createWorkbookUrl: () => string;

  extractData: (
    file: File,
    { onSuccess, onFailure }: Pick<HttpEvent<ExtractExcelDataResponse>, "onSuccess" | "onFailure">,
  ) => HttpEvent<ExtractExcelDataResponse>;
};

export const excelApi: ExcelApi = {
  createWorkbookUrl: () => "/excel/create-workbook",

  extractData: (file, { onSuccess, onFailure }) => {
    const data = new FormData();
    data.append("file", file);
    return httpPost({
      url: "/excel/extract-data",
      data,
      onSuccess,
      onFailure,
    });
  },
};

const A = 65;
const LETTERS = 26;

export const excelColumnName = (colIndex: number): string => {
  const rest = Math.floor(colIndex / LETTERS);
  const char = String.fromCharCode(A + (colIndex % LETTERS));
  if (rest) {
    return `${excelColumnName(rest - 1)}${char}`;
  }
  return char;
};

export const excelColumnAddress = (colIndex: number, rowIndex: number) =>
  `${excelColumnName(colIndex)}${rowIndex + 1}`;

const flattenColumns = <T>(columns: TableColumn<T>[]): TableColumn<T>[] =>
  columns.flatMap((column) => column.children ?? [column]);

export type ExcelExportPurpose = "edit" | "download";

const idColumn = <T>(dataTable: DataTableProps<T>): TableColumn<T> => ({
  label: "ID",
  name: "id",
  exportToExcel: (row) => ({
    type: "string",
    value: String(dataTable.getId(row)),
    readonly: true,
  }),
});

export const exportExcelData = <T>(
  dataTable: DataTableProps<T>,
  i18nState: I18nState,
  purpose: ExcelExportPurpose = "edit",
): ExcelData => {
  const isEdit = purpose === "edit";
  const i18n = prepI18nState(i18nState, isEdit ? Language.EN : i18nState.lang);
  const tableColumns = flattenColumns(dataTable.tableColumns);
  const exported = tableColumns.filter((c) => c.exportToExcel !== undefined);
  const cols = isEdit ? [...exported, idColumn(dataTable)] : exported;
  const rows = sortTableData(
    tableColumns,
    dataTable.tableData,
    dataTable.sort,
    prepI18nState(i18nState),
  );
  return {
    columns: cols.map((c) => ({
      name: c.name,
      label: i18n.tf(c.exportToExcelLabel ?? c.label),
      constraints: c.excelColumnConstraints?.(i18n),
    })),
    rows: rows.map((r, ri) => ({
      cells: cols.map((c, ci) => ({
        readonly: isCellReadonly(c, r, ri),
        ...c.exportToExcel!(
          r,
          {
            row: ri + 1,
            col: ci,
            name: excelColumnName,
            addr: excelColumnAddress,
          },
          i18n,
        ),
      })),
    })),
  };
};

export const importExcelData = action(
  <T>(
    dataTable: DataTableProps<T>,
    data: ExcelData,
    i18nState: I18nState,
  ): { added: T[]; updated: T[] } => {
    const i18n = prepI18nState(i18nState, Language.EN);
    const columnIndices = Object.fromEntries(data.columns.map((c, i) => [c.name, i]));
    return data.rows.reduce<{ added: T[]; updated: T[] }>(
      ({ added, updated }, rowData) => {
        const rowId = get(rowData.cells, [columnIndices["id"], "value"]);
        const existingRow =
          rowId !== undefined
            ? dataTable.tableData.find((x) => String(dataTable.getId(x)) === String(rowId))
            : undefined;
        const targetRow = existingRow ?? dataTable?.initNewRow?.();
        if (targetRow) {
          flattenColumns(dataTable.tableColumns).forEach((col) => {
            const cellData = rowData.cells[columnIndices[col.name]];
            if (cellData && col.importFromExcel) {
              col.importFromExcel(targetRow, cellData, { isNewRow: !existingRow, i18n });
            }
          });
        }
        if (existingRow) {
          updated.push(existingRow);
        } else if (targetRow) {
          added.push(targetRow);
        }
        return { added, updated };
      },
      { added: [], updated: [] },
    );
  },
);

export const editWithExcelEvent = <T>(
  filename: string,
  dataTable: DataTableProps<T>,
  i18n: I18nState,
): DCEvent => ({
  type: "download-file",
  method: "post",
  url: excelApi.createWorkbookUrl(),
  data: { filename, data: exportExcelData(dataTable, i18n) } satisfies CreateExcelWorkbookRequest,
});

export const downloadExcelEvent = <T>(
  filename: string,
  dataTable: DataTableProps<T>,
  i18n: I18nState,
  callbacks: Pick<DownloadFileEvent, "onSuccess" | "onFailure"> = {},
): DownloadFileEvent => ({
  type: "download-file",
  method: "post",
  url: excelApi.createWorkbookUrl(),
  data: {
    filename,
    data: exportExcelData(dataTable, i18n, "download"),
  } satisfies CreateExcelWorkbookRequest,
  ...callbacks,
});

export const importFromExcelEvent = <T>(
  file: File,
  fileUploadConfig: FileUploadConfig,
  dataTable: DataTableProps<T>,
  i18n: I18nState,
  onSuccess: (data: { added: T[]; updated: T[] }) => DCEvent[],
): DCEvent => ({
  type: "validate-files",
  files: [file],
  fileUploadConfig,
  onSuccess: [
    excelApi.extractData(file, {
      onSuccess: ({ data }) => [
        thunk((dispatch) => {
          try {
            const importResult = importExcelData(dataTable, data.data, i18n);
            dispatch(onSuccess(importResult));
          } catch (err) {
            logError(err);
          }
        }),
      ],
      onFailure: () => [showGeneralError()],
    }),
  ],
  onFailure: (msg) => [showError(msg)],
});
